import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";

const AssignmentsList = () => {
  const [assignments, setAssignments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const navigate = useNavigate();
  const { role, token } = useContext(AuthContext);

  // Fetch teacher/student subjects for dropdown
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!token) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSubjects(res.data.subjects || []); // fetch teacher/student subjects
      } catch (err) {
        console.error("Error fetching subjects:", err);
      }
    };
    fetchSubjects();
  }, [token]);

  // Fetch assignments
  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      try {
        let url = `${process.env.REACT_APP_API_URL}/api/assignments`;
        if (selectedSubject) {
          url += `?subject=${encodeURIComponent(selectedSubject)}`;
        }
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Get current date (start of today)
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        // Filter assignments that are overdue (due date < current date)
        const overdueAssignments = response.data.filter((assignment) => {
          const dueDate = new Date(assignment.dueDate);
          dueDate.setHours(0, 0, 0, 0);

          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);

          // Calculate difference in days
          const timeDiff = currentDate - dueDate;
          const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

          // Return assignments overdue by exactly 1 day OR active (due today or future)
          return daysDiff === 1 || dueDate >= currentDate;
        });

        if (role === "teacher") setAssignments(response.data);
        else setAssignments(overdueAssignments);
      } catch (error) {
        console.error("Error fetching assignments:", error);
        setSnackbar({
          open: true,
          message: "Failed to load assignments.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchAssignments();
  }, [token, selectedSubject]);

  const handleDelete = async (assignmentId) => {
    if (!token) {
      setSnackbar({
        open: true,
        message: "You must be logged in to delete assignments.",
        severity: "warning",
      });
      return;
    }
    if (!window.confirm("Are you sure you want to delete this assignment?"))
      return;

    setLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/assignments/${assignmentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAssignments((prev) => prev.filter((a) => a._id !== assignmentId));
      setSnackbar({
        open: true,
        message: "Assignment deleted successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting assignment:", error);
      setSnackbar({
        open: true,
        message: "Error deleting assignment.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getSubjectColor = (subject) => {
    const colors = {
      Mathematics: "#667eea",
      Physics: "#764ba2",
      Chemistry: "#f093fb",
      Biology: "#f5576c",
      English: "#4facfe",
      History: "#43e97b",
    };
    return colors[subject] || "#667eea";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "Overdue";
    if (diffDays === 0) return "Due Today";
    if (diffDays === 1) return "Due Tomorrow";
    return `${diffDays} days left`;
  };

  const getDueDateColor = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    // console.log(diffDays)

    if (diffDays < 0) return "#ef4444";
    if (diffDays <= 1) return "#f59e0b";
    if (diffDays <= 3) return "#f59e0b";
    return "#10b981";
  };

  return (
    <div className="assignments-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .assignments-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .assignments-container::before {
          content: '';
          position: absolute;
          top: 12%;
          right: 8%;
          width: 160px;
          height: 160px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 5s ease-in-out infinite;
        }

        .assignments-container::after {
          content: '';
          position: absolute;
          bottom: 15%;
          left: 10%;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.03);
          animation: float 4s ease-in-out infinite;
          animation-delay: 2.5s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes assignmentSlide {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes slideInFromBottom {
          from { 
            opacity: 0; 
            transform: translateY(100px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        .pulse-element {
          position: absolute;
          top: 40%;
          right: 25%;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.04);
          animation: pulse 3.5s ease-in-out infinite;
        }

        .assignments-wrapper {
          max-width: 1000px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1.1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .controls-section {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: slideIn 0.5s ease-out 0.3s both;
        }

        .controls-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
          border-radius: 20px 20px 0 0;
        }

        .controls-grid {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 1.5rem;
          align-items: end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .filter-label {
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .filter-select {
          padding: 1rem;
          border: 2px solid rgba(209, 213, 219, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          cursor: pointer;
        }

        .filter-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .action-btn {
          padding: 1rem 2rem;
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 15px rgba(254, 107, 139, 0.3);
          white-space: nowrap;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(254, 107, 139, 0.4);
        }

        .assignments-grid {
          display: grid;
          gap: 1.5rem;
        }

        .assignment-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 18px;
          padding: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          animation: assignmentSlide 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .assignment-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px rgba(0,0,0,0.15);
        }

        .assignment-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: var(--subject-color, linear-gradient(180deg, #667eea, #764ba2));
        }

        .assignment-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }

        .assignment-info {
          flex: 1;
        }

        .assignment-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-right: 1rem;
          flex-shrink: 0;
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3);
        }

        .assignment-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }

        .assignment-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #6b7280;
        }

        .subject-badge {
          background: var(--subject-color, #667eea);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        .due-badge {
          background: var(--due-color, #10b981);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        .assignment-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          padding-top: 1rem;
          border-top: 1px solid rgba(0,0,0,0.05);
        }

        .download-btn {
          flex: 1;
          padding: 0.85rem 1.5rem;
          background: linear-gradient(45deg, #10b981 30%, #059669 90%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          text-decoration: none;
        }

        .download-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
          color: white;
          text-decoration: none;
        }

        .delete-btn {
          width: 44px;
          height: 44px;
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .delete-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: translateY(-2px);
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 4rem;
        }

        .loading-content {
          text-align: center;
          color: white;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.5rem;
        }

        .empty-description {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
        }

        .snackbar {
          position: fixed;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          padding: 1rem 2rem;
          border-radius: 12px;
          color: white;
          font-weight: 500;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          backdrop-filter: blur(10px);
          z-index: 1000;
          animation: slideInFromBottom 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          max-width: 400px;
        }

        .snackbar.success {
          background: rgba(16, 185, 129, 0.9);
        }

        .snackbar.error {
          background: rgba(239, 68, 68, 0.9);
        }

        .snackbar.warning {
          background: rgba(245, 158, 11, 0.9);
        }

        .snackbar.info {
          background: rgba(59, 130, 246, 0.9);
        }

        .snackbar-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          opacity: 0.8;
        }

        .snackbar-close:hover {
          opacity: 1;
          background: rgba(255, 255, 255, 0.1);
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .assignments-container {
            padding: 1rem 0.5rem;
          }
          
          .controls-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .assignment-card {
            padding: 1.5rem;
          }
          
          .assignment-header {
            flex-direction: column;
            gap: 1rem;
          }
          
          .assignment-meta {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .assignment-actions {
            flex-direction: column;
            gap: 0.5rem;
          }

          .snackbar {
            left: 1rem;
            right: 1rem;
            transform: none;
            max-width: none;
          }
        }
      `}</style>

      <div className="pulse-element"></div>

      <div className="assignments-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            Academic Dashboard
          </div>

          <h1 className="main-title">
            <span className="gradient-text">Assignment</span> Center
          </h1>

          <p className="subtitle">
            Track, submit, and manage your educational assignments
          </p>
        </div>

        {/* Controls Section */}
        <div className="controls-section">
          <div className="controls-grid">
            <div className="filter-group">
              <label className="filter-label">Filter by Subject</label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="filter-select"
              >
                <option value="">All Subjects</option>
                {subjects.map((subj, idx) => (
                  <option key={idx} value={subj}>
                    {subj}
                  </option>
                ))}
              </select>
            </div>

            {role === "teacher" && (
              <button
                className="action-btn"
                onClick={() => navigate("/teacher-assignment-create")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                </svg>
                Upload Assignment
              </button>
            )}

            {role === "student" && (
              <button
                className="action-btn"
                onClick={() => navigate("/student-assignment-submit")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                </svg>
                Submit Assignment
              </button>
            )}
          </div>
        </div>

        {/* Assignments Display */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              <p>Loading assignments...</p>
            </div>
          </div>
        ) : assignments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </div>
            <h3 className="empty-title">No Assignments Available</h3>
            <p className="empty-description">
              {selectedSubject
                ? `No assignments found for ${selectedSubject}. Try selecting a different subject.`
                : "There are no assignments available at the moment. Check back later for updates."}
            </p>
          </div>
        ) : (
          <div className="assignments-grid">
            {assignments.map((assignment, index) => (
              <div
                key={assignment._id}
                className="assignment-card"
                style={{
                  "--subject-color": getSubjectColor(assignment.subject),
                  "--due-color": getDueDateColor(assignment.dueDate),
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                <div className="assignment-header">
                  <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <div className="assignment-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </div>
                    <div className="assignment-info">
                      <h3 className="assignment-title">{assignment.title}</h3>
                    </div>
                  </div>
                </div>

                <div className="assignment-meta">
                  <div className="meta-item">
                    <div
                      className="subject-badge"
                      style={{
                        background: getSubjectColor(assignment.subject),
                      }}
                    >
                      {assignment.subject}
                    </div>
                  </div>
                  <div className="meta-item">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M19,19V5H5V19H19M17,12H12V17H17V12Z" />
                    </svg>
                    Due: {new Date(assignment.dueDate).toLocaleDateString()}
                  </div>
                  <div className="meta-item">
                    <div
                      className="due-badge"
                      style={{
                        background: getDueDateColor(assignment.dueDate),
                      }}
                    >
                      {formatDate(assignment.dueDate)}
                    </div>
                  </div>
                </div>

                <div className="assignment-actions">
                  <button
                    className="download-btn"
                    onClick={() => window.open(assignment.fileUrl, "_blank")}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                    </svg>
                    Download
                  </button>

                  {role === "teacher" && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(assignment._id)}
                      title="Delete assignment"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Snackbar Notification */}
      {snackbar.open && (
        <div className={`snackbar ${snackbar.severity}`}>
          <span>{snackbar.message}</span>
          <button className="snackbar-close" onClick={closeSnackbar}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default AssignmentsList;
